/**
 * Google Search Console panel — fetches all four data shapes in parallel and
 * lays out KPIs / top queries / top pages / striking-distance opportunities.
 *
 * Mirrors the GA4Panel layout intentionally so the two tabs feel like one feature.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import {
  MousePointerClick, Eye, Percent, ListOrdered, Sparkles, ExternalLink, Settings, Search, ChevronUp, ChevronDown, ChevronsUpDown,
  Plus, CheckSquare, Square, BookmarkCheck, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useSyncedRange, RANGE_HEADING } from "../../lib/useSyncedRange";
import RangeToggle from "./RangeToggle";

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(Math.round(n));
}

const fmtPct = (n) => (n == null ? "—" : `${(n * 100).toFixed(1)}%`);
const fmtPos = (n) => (n == null ? "—" : n.toFixed(1));

function pctDelta(cur, prev) {
  if (prev == null || prev === 0) return null;
  return ((cur - prev) / prev) * 100;
}

function DeltaChip({ value, invert }) {
  // `invert` = true means "lower is better" (e.g. average position) — flip the colour
  if (value == null) return <span className="text-[10px] text-[#a0aec0] font-mono-tight">—</span>;
  const numerically_positive = value >= 0;
  const semantically_positive = invert ? !numerically_positive : numerically_positive;
  const color = semantically_positive ? "#48bb78" : "#f56565";
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-md font-mono-tight"
      style={{ background: color + "1a", color }}
    >
      {numerically_positive ? "▲" : "▼"} {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function KpiCard({ icon: Icon, label, current, previous, format = fmtNum, sparkData, sparkKey, invertDelta = false, prevLabel, testid }) {
  const delta = pctDelta(current, previous);
  return (
    <div className="card-soft p-5 flex flex-col gap-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center">
            <Icon className="h-4 w-4" strokeWidth={2.4} />
          </span>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">{label}</div>
        </div>
        <DeltaChip value={delta} invert={invertDelta} />
      </div>
      <div>
        <div className="font-mono-tight text-2xl text-[#1a202c]">{format(current)}</div>
        <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-0.5">
          vs {format(previous)} {prevLabel || "prior 28d"}
        </div>
      </div>
      <div className="h-10 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={sparkData || []} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
            <YAxis hide domain={["dataMin", "dataMax"]} reversed={invertDelta} />
            <Tooltip
              contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 11, padding: "4px 8px" }}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ""}
              formatter={(v) => [format(v), label]}
            />
            <Line type="monotone" dataKey={sparkKey} stroke="#4fd1c5" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SortIcon({ active, dir }) {
  const Icon = !active ? ChevronsUpDown : dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <Icon
      className={`h-3 w-3 transition-opacity ${
        active ? "opacity-100 text-[#2c7a7b]" : "opacity-40 group-hover/sort:opacity-80"
      }`}
      strokeWidth={2.5}
    />
  );
}

function SortHeader({ label, sortKey, currentKey, currentDir, onClick, align = "left", title }) {
  const active = currentKey === sortKey;
  return (
    <th className={`px-3 py-2.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold whitespace-nowrap ${
      align === "right" ? "text-right" : "text-left"
    }`}>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        title={title || `Sort by ${label}`}
        className={`inline-flex items-center gap-1 hover:text-[#1a202c] transition-colors group/sort ${
          active ? "text-[#1a202c]" : ""
        }`}
      >
        <span>{label}</span>
        <SortIcon active={active} dir={currentDir} />
      </button>
    </th>
  );
}

/** A sortable, searchable table that renders the GSC query/page row shape.
 * When `selection` prop is passed, renders a checkbox column that lets the
 * user pick queries to bulk-add to the client's tracked keywords list.
 * Rows for already-tracked queries render a "Tracked" badge instead of a
 * checkbox so users can see at a glance what's already in the rank tracker. */
function GscDataTable({
  rows, dimensionKey, dimensionLabel, testid, query, onQueryChange, showPage = false,
  selection, // { selected: Set, toggleOne, toggleAll, trackedSet, selectableCount }
}) {
  const [sortKey, setSortKey] = useState("clicks");
  const [sortDir, setSortDir] = useState("desc");
  const enableSelect = !!selection;

  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      // Sensible default: text dims default asc, numbers default desc
      setSortDir(key === dimensionKey ? "asc" : "desc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else if (sortDir === "desc") {
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r[dimensionKey] || "").toLowerCase().includes(q));
  }, [rows, query, dimensionKey]);

  const sorted = useMemo(() => {
    const decorated = filtered.map((r, i) => ({ r, i }));
    decorated.sort((a, b) => {
      const av = a.r[sortKey];
      const bv = b.r[sortKey];
      const aNull = av == null;
      const bNull = bv == null;
      if (aNull && bNull) return a.i - b.i;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        const cmp = av.localeCompare(bv);
        return sortDir === "asc" ? cmp : -cmp;
      }
      if (av === bv) return a.i - b.i;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return decorated.map((d) => d.r);
  }, [filtered, sortKey, sortDir]);

  // Master checkbox: reflects whether every selectable row in the CURRENT
  // filtered view is picked. We derive this each render so search filtering
  // and the master checkbox stay in sync.
  const filteredSelectable = useMemo(
    () => enableSelect
      ? sorted.filter((r) => r[dimensionKey] && !selection.trackedSet.has(r[dimensionKey].toLowerCase()))
      : [],
    [sorted, dimensionKey, selection, enableSelect],
  );
  const allInViewChecked =
    enableSelect &&
    filteredSelectable.length > 0 &&
    filteredSelectable.every((r) => selection.selected.has(r[dimensionKey]));

  const colSpan = (showPage ? 6 : 5) + (enableSelect ? 1 : 0);

  return (
    <div className="card-soft overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-[#edf2f7] flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#a0aec0] pointer-events-none" />
          <input
            type="text"
            value={query || ""}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={`Filter ${dimensionLabel.toLowerCase()}…`}
            data-testid={`${testid}-search`}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[#edf2f7] bg-white text-[#1a202c] placeholder:text-[#a0aec0] focus:outline-none focus:border-[#4fd1c5] focus:ring-2 focus:ring-[#4fd1c5]/20 transition-colors"
          />
        </div>
        <div className="text-xs font-mono-tight text-[#a0aec0]">
          {query ? (
            <span><span className="text-[#1a202c] font-semibold">{sorted.length}</span> of {rows.length}</span>
          ) : (
            <span>{rows.length} {dimensionLabel.toLowerCase()}</span>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f7fafc] border-b border-[#edf2f7]">
            <tr>
              {enableSelect && (
                <th className="px-3 py-2.5 w-8">
                  <button
                    type="button"
                    onClick={() => selection.toggleAll(filteredSelectable.map((r) => r[dimensionKey]))}
                    disabled={filteredSelectable.length === 0}
                    title={
                      filteredSelectable.length === 0
                        ? "Nothing left to add (everything is already tracked)"
                        : allInViewChecked ? "Deselect all" : "Select all"
                    }
                    data-testid={`${testid}-select-all`}
                    className="inline-flex items-center justify-center text-[#a0aec0] hover:text-[#2c7a7b] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    {allInViewChecked ? (
                      <CheckSquare className="h-4 w-4 text-[#2c7a7b]" strokeWidth={2.2} />
                    ) : (
                      <Square className="h-4 w-4" strokeWidth={2.2} />
                    )}
                  </button>
                </th>
              )}
              <SortHeader label={dimensionLabel} sortKey={dimensionKey} currentKey={sortKey} currentDir={sortDir} onClick={handleSort} />
              {showPage && (
                <SortHeader label="Page" sortKey="page" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} title="Top landing page for this query (by impressions)" />
              )}
              <SortHeader label="Clicks" sortKey="clicks" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} align="right" />
              <SortHeader label="Impressions" sortKey="impressions" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} align="right" />
              <SortHeader label="CTR" sortKey="ctr" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} align="right" />
              <SortHeader label="Avg pos" sortKey="position" currentKey={sortKey} currentDir={sortDir} onClick={handleSort} align="right" title="Lower is better" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#edf2f7]">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-5 py-6 text-center text-xs text-[#a0aec0]">
                  {rows.length === 0 ? "No data available for the selected period." : `No matches for "${query}".`}
                </td>
              </tr>
            ) : (
              sorted.map((r, i) => {
                const isPage = dimensionKey === "page";
                const label = r[dimensionKey] || "";
                const pageUrl = r.page || "";
                const pageShort = pageUrl ? pageUrl.replace(/^https?:\/\//, "") : "";
                const isTracked = enableSelect && label && selection.trackedSet.has(label.toLowerCase());
                const isChecked = enableSelect && selection.selected.has(label);
                return (
                  <tr key={`${label}-${i}`} className={`hover:bg-[#f7fafc]/40 ${isTracked ? "bg-[#f7fafc]/60" : ""}`}>
                    {enableSelect && (
                      <td className="px-3 py-2 w-8">
                        {isTracked ? (
                          <span
                            title="Already in the rank tracker"
                            data-testid={`${testid}-tracked-${label}`}
                            className="inline-flex items-center justify-center h-4 w-4 rounded bg-[#4fd1c5]/15 text-[#2c7a7b]"
                          >
                            <BookmarkCheck className="h-3 w-3" strokeWidth={2.4} />
                          </span>
                        ) : label ? (
                          <button
                            type="button"
                            onClick={() => selection.toggleOne(label)}
                            data-testid={`${testid}-checkbox-${label}`}
                            className="inline-flex items-center justify-center text-[#a0aec0] hover:text-[#2c7a7b] transition-colors"
                          >
                            {isChecked ? (
                              <CheckSquare className="h-4 w-4 text-[#2c7a7b]" strokeWidth={2.2} />
                            ) : (
                              <Square className="h-4 w-4" strokeWidth={2.2} />
                            )}
                          </button>
                        ) : null}
                      </td>
                    )}
                    <td className="px-3 py-2 text-[#1a202c] max-w-[420px]">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={`truncate ${isTracked ? "text-[#718096]" : ""}`} title={label}>{label}</span>
                        {isPage && label && (
                          <a
                            href={label}
                            target="_blank"
                            rel="noreferrer"
                            title="Open page"
                            className="shrink-0 text-[#a0aec0] hover:text-[#2c7a7b] transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </td>
                    {showPage && (
                      <td className="px-3 py-2 text-[#4a5568] max-w-[320px]">
                        {pageUrl ? (
                          <a
                            href={pageUrl}
                            target="_blank"
                            rel="noreferrer"
                            title={pageUrl}
                            className="inline-flex items-center gap-1.5 text-xs font-mono-tight text-[#4a5568] hover:text-[#2c7a7b] transition-colors min-w-0"
                          >
                            <span className="truncate">{pageShort.length > 44 ? pageShort.slice(0, 44) + "…" : pageShort}</span>
                            <ExternalLink className="h-3 w-3 shrink-0 opacity-60" strokeWidth={2.2} />
                          </a>
                        ) : (
                          <span className="text-[#cbd5e0] text-xs font-mono-tight" title="GSC didn't return a top page for this query in the last 28d">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right font-mono-tight font-semibold text-[#1a202c]">{fmtNum(r.clicks)}</td>
                    <td className="px-3 py-2 text-right font-mono-tight text-[#4a5568]">{fmtNum(r.impressions)}</td>
                    <td className="px-3 py-2 text-right font-mono-tight text-[#4a5568]">{fmtPct(r.ctr)}</td>
                    <td className="px-3 py-2 text-right font-mono-tight text-[#4a5568]">{fmtPos(r.position)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function GscPanel({ client, reloadClient }) {
  const clientId = client?.id;
  const [state, setState] = useState({
    loading: true,
    overview: null,
    queries: [],
    pages: [],
    opportunities: [],
    configured: false,
    error: null,
  });
  const [queriesFilter, setQueriesFilter] = useState("");
  const [pagesFilter, setPagesFilter] = useState("");
  const [oppsFilter, setOppsFilter] = useState("");
  const [range, setRange] = useSyncedRange();

  // Shared selection state across BOTH query tables (opportunities + top
  // queries often overlap — a single set means the user's picks survive
  // switching between them and the counter reflects the total unique picks).
  const [selectedQueries, setSelectedQueries] = useState(() => new Set());
  const [adding, setAdding] = useState(false);

  // Reset selection whenever the client changes so we don't carry picks
  // across clients (the tracked-set would be wrong).
  useEffect(() => {
    setSelectedQueries(new Set());
  }, [clientId]);

  const trackedSet = useMemo(
    () => new Set((client?.keywords || []).map((k) => (k || "").toLowerCase())),
    [client?.keywords],
  );

  const toggleOne = (q) => {
    setSelectedQueries((prev) => {
      const next = new Set(prev);
      if (next.has(q)) next.delete(q);
      else next.add(q);
      return next;
    });
  };

  // `visibleSelectable` is the list of queries the master checkbox should
  // toggle — computed by the child table using its current filter/sort, so
  // "Select all" only affects what's actually on screen.
  const toggleAll = (visibleSelectable) => {
    setSelectedQueries((prev) => {
      const next = new Set(prev);
      const allOn = visibleSelectable.length > 0 && visibleSelectable.every((q) => next.has(q));
      if (allOn) {
        for (const q of visibleSelectable) next.delete(q);
      } else {
        for (const q of visibleSelectable) next.add(q);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedQueries(new Set());

  // Scope-aware add — writes to `local_keywords` for local campaigns and
  // `national_keywords` otherwise. The backend rebuilds the flat `keywords`
  // union server-side (see _apply_keyword_split_and_limit in server.py) so
  // the rank tracker picks the new keywords up automatically.
  const addToTracked = async () => {
    if (selectedQueries.size === 0 || !client) return;
    const isLocal = (client.campaign_type || "national") === "local";
    const targetField = isLocal ? "local_keywords" : "national_keywords";
    const existing = client[targetField] || [];
    const seen = new Set(existing.map((k) => (k || "").toLowerCase()));
    // Also treat anything in the flat `keywords` list as already-tracked
    // so we never add duplicates from the other scope.
    for (const k of client.keywords || []) seen.add((k || "").toLowerCase());
    const additions = [];
    for (const q of selectedQueries) {
      const trimmed = (q || "").trim();
      const lo = trimmed.toLowerCase();
      if (!trimmed || seen.has(lo)) continue;
      seen.add(lo);
      additions.push(trimmed);
    }
    if (additions.length === 0) {
      toast.info("Those queries are already tracked");
      clearSelection();
      return;
    }
    setAdding(true);
    try {
      await api.patch(`/clients/${client.id}`, {
        [targetField]: [...existing, ...additions],
      });
      toast.success(
        `Added ${additions.length} keyword${additions.length > 1 ? "s" : ""} to ${isLocal ? "local" : "national"} rank tracker`,
      );
      clearSelection();
      reloadClient?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add keywords");
    } finally {
      setAdding(false);
    }
  };

  const selection = client ? {
    selected: selectedQueries,
    toggleOne,
    toggleAll,
    trackedSet,
  } : null;

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.all([
      api.get(`/clients/${clientId}/gsc/overview`, { params: { range } }),
      api.get(`/clients/${clientId}/gsc/queries`, { params: { limit: 100, range } }),
      api.get(`/clients/${clientId}/gsc/pages`, { params: { limit: 50, range } }),
      api.get(`/clients/${clientId}/gsc/opportunities`, { params: { range } }),
    ])
      .then(([ov, q, p, o]) => {
        if (cancelled) return;
        setState({
          loading: false,
          overview: ov.data?.configured ? ov.data : null,
          configured: !!ov.data?.configured,
          queries: q.data?.rows || [],
          pages: p.data?.rows || [],
          opportunities: o.data?.rows || [],
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          overview: null,
          queries: [],
          pages: [],
          opportunities: [],
          configured: false,
          error: err?.response?.data?.detail || "Failed to load Search Console data",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, range]);

  if (state.loading) {
    return <div className="text-sm text-[#a0aec0] font-mono-tight">Loading Search Console…</div>;
  }

  if (state.error) {
    return (
      <div className="card-soft p-6 border border-[#feb2b2] bg-[#fed7d7]/30">
        <div className="font-semibold text-[#742a2a] mb-2">Couldn&apos;t load Search Console data</div>
        <div className="text-sm text-[#742a2a] font-mono-tight">{state.error}</div>
      </div>
    );
  }

  if (!state.configured) {
    return (
      <div className="card-soft p-6 text-center">
        <Settings className="h-6 w-6 text-[#a0aec0] mx-auto mb-3" />
        <div className="font-semibold text-[#1a202c] mb-1">Search Console not connected for this client</div>
        <div className="text-sm text-[#a0aec0] max-w-md mx-auto">
          Open <span className="font-semibold">Edit Client</span> and add a Search Console property
          (you&apos;ll need the Google account to have access to it).
        </div>
      </div>
    );
  }

  const ov = state.overview;
  const cur = ov?.current || {};
  const prev = ov?.previous || {};
  const daily = ov?.daily || [];
  const rangeHeading = RANGE_HEADING[range] || "last 30 days";
  const dateLabel = ov?.date_range ? `${ov.date_range.start} → ${ov.date_range.end}` : rangeHeading;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
            Search Console · {rangeHeading}
          </div>
          <div className="text-lg font-semibold text-[#1a202c]">{ov?.site_url || "—"}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <RangeToggle range={range} onChange={setRange} disabled={state.loading} testid="gsc-range-toggle" />
          <div className="text-[10px] text-[#a0aec0] font-mono-tight">
            {dateLabel} · 2-3 day lag
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={MousePointerClick}
          label="Clicks"
          current={cur.clicks}
          previous={prev.clicks}
          sparkData={daily}
          sparkKey="clicks"
          prevLabel={ov?.range_label}
          testid="gsc-kpi-clicks"
        />
        <KpiCard
          icon={Eye}
          label="Impressions"
          current={cur.impressions}
          previous={prev.impressions}
          sparkData={daily}
          sparkKey="impressions"
          prevLabel={ov?.range_label}
          testid="gsc-kpi-impressions"
        />
        <KpiCard
          icon={Percent}
          label="CTR"
          current={cur.ctr}
          previous={prev.ctr}
          format={fmtPct}
          sparkData={daily}
          sparkKey="ctr"
          prevLabel={ov?.range_label}
          testid="gsc-kpi-ctr"
        />
        <KpiCard
          icon={ListOrdered}
          label="Avg position"
          current={cur.position}
          previous={prev.position}
          format={fmtPos}
          sparkData={daily}
          sparkKey="position"
          invertDelta
          prevLabel={ov?.range_label}
          testid="gsc-kpi-position"
        />
      </div>

      {state.opportunities.length > 0 && (
        <div className="card-soft p-5" data-testid="gsc-opportunities-card">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4 text-[#dd6b20]" />
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Striking-distance opportunities</div>
          </div>
          <div className="text-sm text-[#4a5568] mb-3 max-w-2xl">
            Queries currently ranking <span className="font-semibold">positions 4-15</span> with at least 20 impressions in the {rangeHeading} — the lowest-hanging fruit for on-page SEO work. Boost CTR with better titles &amp; meta descriptions, nudge position up by improving relevance.
          </div>
          <GscDataTable
            rows={state.opportunities}
            dimensionKey="query"
            dimensionLabel="Query"
            testid="gsc-opportunities-table"
            query={oppsFilter}
            onQueryChange={setOppsFilter}
            showPage
            selection={selection}
          />
        </div>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold mb-2">Top queries ({rangeHeading})</div>
        <GscDataTable
          rows={state.queries}
          dimensionKey="query"
          dimensionLabel="Query"
          testid="gsc-top-queries"
          query={queriesFilter}
          onQueryChange={setQueriesFilter}
          selection={selection}
        />
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold mb-2">Top landing pages ({rangeHeading})</div>
        <GscDataTable
          rows={state.pages}
          dimensionKey="page"
          dimensionLabel="Page"
          testid="gsc-top-pages"
          query={pagesFilter}
          onQueryChange={setPagesFilter}
        />
      </div>

      {/* Floating "Add to rank tracker" action bar — sticks to the bottom of
          the viewport whenever ≥1 query is selected in either table above.
          Uses the client's campaign_type to pick national vs local scope. */}
      {selection && selectedQueries.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 card-soft border border-[#4fd1c5]/40 shadow-[0_12px_32px_-8px_rgba(45,55,72,0.25)] px-5 py-3 flex items-center gap-4 bg-white"
          data-testid="gsc-add-to-tracked-bar"
        >
          <div className="text-sm text-[#4a5568]">
            <span className="font-bold text-[#1a202c] font-mono-tight" data-testid="gsc-selection-count">
              {selectedQueries.size}
            </span>{" "}
            {selectedQueries.size === 1 ? "query" : "queries"} selected
            <span className="text-[#a0aec0]"> · will add to </span>
            <span className="font-semibold text-[#2c7a7b]">
              {(client?.campaign_type || "national") === "local" ? "local" : "national"}
            </span>
            <span className="text-[#a0aec0]"> rank tracker</span>
          </div>
          <button
            type="button"
            onClick={clearSelection}
            className="text-xs text-[#a0aec0] hover:text-[#4a5568] transition-colors"
            data-testid="gsc-clear-selection"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={addToTracked}
            disabled={adding}
            data-testid="gsc-add-to-tracked-btn"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {adding ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" strokeWidth={2.4} />
            )}
            Add {selectedQueries.size} to tracked
          </button>
        </div>
      )}
    </div>
  );
}
